'use client';

import { MOBILE_QUERY, useMediaQuery } from '@frontend/utils/UseMediaQuery';
import { PropsWithChildren, useEffect } from 'react';
import { useAccount, useConnect } from 'wagmi';

interface AutoConnectOptions {
  enableOnMobile?: boolean;
  enableOnDesktop?: boolean;
  retryAttempts?: number;
}

export function AutoConnectProvider({
  children,
  options = {
    enableOnMobile: true,
    enableOnDesktop: false,
    retryAttempts: 1,
  },
}: PropsWithChildren<{ options?: AutoConnectOptions }>) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const { connectAsync, connectors } = useConnect();
  const { isConnected, isReconnecting } = useAccount();

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;

    const shouldAutoConnect = () => {
      if (isConnected || isReconnecting) return false;
      if (isMobile && !options.enableOnMobile) return false;
      if (!isMobile && !options.enableOnDesktop) return false;
      return true;
    };

    const attemptAutoConnect = async () => {
      if (!shouldAutoConnect() || !connectors.length) return;

      try {
        // Use the first available connector (usually injected)
        const connector =
          connectors.find((c) => c.type === 'injected') || connectors[0];

        if (connector) {
          await connectAsync({ connector });
        }
      } catch (error) {
        console.warn('Auto-connect failed:', error);

        if (retryCount < (options.retryAttempts || 1)) {
          retryCount++;
          setTimeout(() => {
            if (isMounted) attemptAutoConnect();
          }, 1000);
        } else {
          console.error(error);
        }
      }
    };

    // Delay auto-connect to avoid hydration issues
    const timeoutId = setTimeout(attemptAutoConnect, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [
    isMobile,
    connectAsync,
    connectors,
    options.enableOnMobile,
    options.enableOnDesktop,
    options.retryAttempts,
  ]);

  return <>{children}</>;
}
